import { graphql } from '../gql';
import { GetPublicWhiteLabelSettingsDocument } from '../gql/graphql';

export const UPDATE_WHITE_LABEL_SETTINGS = graphql(`
  mutation updateWhiteLabelSettings(
    $token: String!
    $slug: String!
    $newSlug: String
    $host: String
    $companyName: String
    $phone: String
    $email: String
    $hours: String
    $chatLink: String
    $privacyLink: String
    $title: String
    $thankYouText: String
    $policystreamIntroductoryText: String
    $homepage: String
    $googleAnalyticsToken: String
    $includeCustomShadows: Boolean
    $sendEmailOnContactInformationCompleted: Boolean
    $contactInformationFirst: Boolean
    $allowApplicationUpload: Boolean
    $aiAssistedBusinessSelection: Boolean
    $palette: PaletteInput
    $typography: TypographyInput
    $types: [String]
    $customButtons: [CustomButtonInput]
  ) {
    updateWhiteLabelSettings(
      token: $token
      slug: $slug
      newSlug: $newSlug
      host: $host
      companyName: $companyName
      phone: $phone
      email: $email
      hours: $hours
      chatLink: $chatLink
      privacyLink: $privacyLink
      title: $title
      thankYouText: $thankYouText
      policystreamIntroductoryText: $policystreamIntroductoryText
      homepage: $homepage
      googleAnalyticsToken: $googleAnalyticsToken
      includeCustomShadows: $includeCustomShadows
      sendEmailOnContactInformationCompleted: $sendEmailOnContactInformationCompleted
      contactInformationFirst: $contactInformationFirst
      allowApplicationUpload: $allowApplicationUpload
      aiAssistedBusinessSelection: $aiAssistedBusinessSelection
      palette: $palette
      typography: $typography
      types: $types
      customButtons: $customButtons
    ) {
      whiteLabelSettings {
        slug
        host
        companyName
        phone
        email
        hours
        chatLink
        privacyLink
        title
        thankYouText
        policystreamIntroductoryText
        homepage
        googleAnalyticsToken
        includeCustomShadows
        sendEmailOnContactInformationCompleted
        contactInformationFirst
        allowApplicationUpload
        aiAssistedBusinessSelection
      }
    }
  }
`);

// Runtime documents come from codegen (correct AST). The `graphql()` calls stay for graphql-codegen scans.
void graphql(`
  fragment WhiteLabelColourSwatchFields on WhiteLabelColourType {
    lighter
    light
    main
    dark
    darker
  }

  fragment WhiteLabelFontNameFields on WhiteLabelFontType {
    name
    family
  }

  fragment WhiteLabelPaletteForSettings on WhiteLabelPaletteType {
    primaryContrastText
    secondaryContrastText
    primary {
      ...WhiteLabelColourSwatchFields
    }
    secondary {
      ...WhiteLabelColourSwatchFields
    }
    success {
      ...WhiteLabelColourSwatchFields
    }
    error {
      ...WhiteLabelColourSwatchFields
    }
    warning {
      ...WhiteLabelColourSwatchFields
    }
    info {
      ...WhiteLabelColourSwatchFields
    }
  }

  fragment WhiteLabelTypographyForSettings on WhiteLabelTypographyType {
    fontFamily {
      ...WhiteLabelFontNameFields
    }
    h1 {
      ...WhiteLabelFontNameFields
    }
    h2 {
      ...WhiteLabelFontNameFields
    }
    h3 {
      ...WhiteLabelFontNameFields
    }
    h4 {
      ...WhiteLabelFontNameFields
    }
    h5 {
      ...WhiteLabelFontNameFields
    }
    h6 {
      ...WhiteLabelFontNameFields
    }
    subtitleFontWeight
  }

  fragment WhiteLabelLogoSlotFileFields on S3FileType {
    id
    logoData
    filename
  }

  # Shared WL row shape for both public and management queries — same selected fields.
  fragment WhiteLabelSettingsRowForManagement on WhiteLabelSettingsType {
    organization {
      id
    }
    id
    slug
    host
    companyName
    phone
    email
    hours
    chatLink
    privacyLink
    title
    thankYouText
    policystreamIntroductoryText
    homepage
    googleAnalyticsToken
    includeCustomShadows
    sendEmailOnContactInformationCompleted
    contactInformationFirst
    allowApplicationUpload
    aiAssistedBusinessSelection
    types {
      type
    }
    palette {
      ...WhiteLabelPaletteForSettings
    }
    typography {
      ...WhiteLabelTypographyForSettings
    }
    customButtons {
      title
      url
      order
    }
    logo {
      ...WhiteLabelLogoSlotFileFields
    }
    favicon {
      ...WhiteLabelLogoSlotFileFields
    }
    mobileLogo {
      ...WhiteLabelLogoSlotFileFields
    }
    squareLogo {
      ...WhiteLabelLogoSlotFileFields
    }
    headerLogo {
      ...WhiteLabelLogoSlotFileFields
    }
    footerLogo {
      ...WhiteLabelLogoSlotFileFields
    }
  }

  # Public defaults + form metadata — safe to fetch without a token.
  # The optional slug argument lets allow-listed hosts (staging / localhost)
  # request a specific white label; the backend ignores it for other origins.
  query getPublicWhiteLabelSettings($slug: String) {
    whiteLabelBehaviouralFlagDefinitions {
      name
      label
    }
    whiteLabelInsuranceTypeChoices {
      value
      label
    }
    whiteLabelPaletteColourRoles
    whiteLabelColourShadeFields
    whiteLabelTypographyHeadingLevels
    publicWhiteLabelSettings(slug: $slug) {
      ...WhiteLabelSettingsRowForManagement
    }
  }

  # Management form query — intentionally does not use public fallback settings.
  query getManagementWhiteLabelSettings {
    whiteLabelBehaviouralFlagDefinitions {
      name
      label
    }
    whiteLabelInsuranceTypeChoices {
      value
      label
    }
    whiteLabelPaletteColourRoles
    whiteLabelColourShadeFields
    whiteLabelTypographyHeadingLevels
    publicWhiteLabelSettings(fallback: false) {
      ...WhiteLabelSettingsRowForManagement
    }
  }
`);

export const GET_PUBLIC_WHITE_LABEL_SETTINGS =
  GetPublicWhiteLabelSettingsDocument;

export const GET_MANAGEMENT_WHITE_LABEL_SETTINGS = graphql(`
  query getManagementWhiteLabelSettings {
    whiteLabelBehaviouralFlagDefinitions {
      name
      label
    }
    whiteLabelInsuranceTypeChoices {
      value
      label
    }
    whiteLabelPaletteColourRoles
    whiteLabelColourShadeFields
    whiteLabelTypographyHeadingLevels
    publicWhiteLabelSettings(fallback: false) {
      ...WhiteLabelSettingsRowForManagement
    }
  }
`);

export const GET_AVAILABLE_FONTS = graphql(`
  query getAvailableFonts {
    availableFontChoices {
      name
      family
    }
  }
`);

export const UPLOAD_WHITE_LABEL_LOGO = graphql(`
  mutation uploadWhiteLabelLogo(
    $token: String!
    $whiteLabelSettingsId: Int!
    $logoField: WhiteLabelLogoField!
    $fileData: String!
    $filename: String!
  ) {
    uploadWhiteLabelLogo(
      token: $token
      whiteLabelSettingsId: $whiteLabelSettingsId
      logoField: $logoField
      fileData: $fileData
      filename: $filename
    ) {
      logoFile {
        id
        logoData
        filename
      }
    }
  }
`);

export const REMOVE_WHITE_LABEL_LOGO = graphql(`
  mutation removeWhiteLabelLogo(
    $token: String!
    $whiteLabelSettingsId: Int!
    $logoField: WhiteLabelLogoField!
  ) {
    removeWhiteLabelLogo(
      token: $token
      whiteLabelSettingsId: $whiteLabelSettingsId
      logoField: $logoField
    ) {
      success
    }
  }
`);

export const SAVE_WHITE_LABEL_LOGOS = graphql(`
  mutation saveWhiteLabelLogos(
    $token: String!
    $whiteLabelSettingsId: Int!
    $changes: [WhiteLabelLogoChangeInput!]!
  ) {
    saveWhiteLabelLogos(
      token: $token
      whiteLabelSettingsId: $whiteLabelSettingsId
      changes: $changes
    ) {
      success
    }
  }
`);
